import { BackendModule, ReadCallback, Services, InitOptions } from "i18next";
import { logger } from "../../utils/logger";
import { API_BASE_URL } from "../common/constants";

interface BackendOptions {
  loadPath: string;
}

/**
 * Custom i18next backend that uses our TranslationService for lazy loading
 */
class LazyLoadBackend implements BackendModule<BackendOptions> {
  static type = "backend" as const;
  type = "backend" as const;

  private services!: Services;
  private options!: InitOptions<BackendOptions>;
  private allTranslationsLoaded = false;

  init(
    services: Services,
    backendOptions: BackendOptions,
    i18nextOptions: InitOptions<BackendOptions>,
  ): void {
    this.services = services;
    this.options = i18nextOptions;
  }

  read(
    language: string,
    namespace: string,
    callback: ReadCallback,
  ): void {
    // Return empty object - translations will be loaded via TranslationService on demand
    // This prevents loading all translations at once on initialization
    callback(null, {});
  }

  private async loadAllTranslations(
    language: string,
    callback: ReadCallback,
  ): Promise<void> {
    try {
      // Fetch all translations for backward compatibility during migration
      const response = await fetch(
        `${API_BASE_URL}/api/translations/${language}`,
      );

      if (!response.ok) {
        // Try parent language
        const parentLanguage = language.split("-")[0];
        if (parentLanguage !== language) {
          const parentResponse = await fetch(
            `${API_BASE_URL}/api/translations/${parentLanguage}`,
          );
          if (parentResponse.ok) {
            const data = await parentResponse.json();
            this.allTranslationsLoaded = true;
            callback(null, data);
            return;
          }
        }

        callback(new Error(`Failed to load translations for ${language}`), undefined);
        return;
      }

      const data = await response.json();
      this.allTranslationsLoaded = true;
      callback(null, data);
    } catch (error) {
      logger.error("Failed to load all translations:", error);
      callback(error as Error, undefined);
    }
  }

  create?(
    languages: string[],
    namespace: string,
    key: string,
    fallbackValue: string,
    callback?: (err: Error | null, t?: string) => void,
  ): void {
    // Not implemented - we don't support creating translations from frontend
    if (callback) {
      callback(new Error("Creating translations is not supported"), undefined);
    }
  }
}

export default LazyLoadBackend;
